import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { mensagemDeErro, novaChave, rpc } from "../../lib/rpc";
import { supabase } from "../../lib/supabase";
import { localPorCodigo, produtoPorCodigo } from "../../lib/leitura";
import { sinalConcluido, sinalErro, sinalOk } from "../../lib/sinais";
import { fmtNum, nomeTipoLocal } from "../../lib/formato";
import { LeituraCodigo } from "../../componentes/LeituraCodigo";
import { AlertaErro } from "../../componentes/Alerta";
import { Botao, Vazio } from "../../componentes/ui";

interface ItemLocal { produto_id: string; quantidade: number; reservado: number; produtos: { codigo: string | null; nome: string } }

/** Transferência entre locais. Também é a devolução da sobra do carrinho para a posição. */
export default function Transferir() {
  const [params] = useSearchParams();
  const [origem, setOrigem] = useState<{ codigo: string; tipo: string } | null>(null);
  const [itens, setItens] = useState<ItemLocal[]>([]);
  const [destino, setDestino] = useState<string | null>(null);
  const [produto, setProduto] = useState<{ codigo: string; produto_id: string; nome: string; disponivel: number } | null>(null);
  const [qtd, setQtd] = useState(1);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [lampejo, setLampejo] = useState(0);

  const carregarOrigem = useCallback(async (codigo: string) => {
    const local = await localPorCodigo(codigo);
    if (!local) {
      sinalErro();
      setErro("Local não cadastrado neste depósito");
      return false;
    }
    const { data } = await supabase.from("saldo_posicao")
      .select("produto_id, quantidade, reservado, produtos(codigo, nome)")
      .eq("local_id", local.id).gt("quantidade", 0);
    setOrigem({ codigo: local.codigo, tipo: local.tipo });
    setItens((data ?? []) as unknown as ItemLocal[]);
    return true;
  }, []);

  useEffect(() => {
    const o = params.get("origem");
    if (o) void carregarOrigem(o);
  }, [params, carregarOrigem]);

  async function leitura(codigo: string) {
    setOk(null);
    if (!origem) {
      if (await carregarOrigem(codigo)) {
        sinalOk();
        setLampejo((n) => n + 1);
      }
      return;
    }
    if (!produto) {
      const p = await produtoPorCodigo(codigo);
      const item = itens.find((i) => i.produto_id === p?.id);
      if (!p || !item) { sinalErro(); return setErro(`Este produto não está em ${origem.codigo}`); }
      sinalOk();
      setLampejo((n) => n + 1);
      const disponivel = item.quantidade - item.reservado;
      setProduto({ codigo, produto_id: item.produto_id, nome: item.produtos.nome, disponivel });
      setQtd(disponivel);
      return;
    }
    if (!destino) {
      sinalOk();
      setLampejo((n) => n + 1);
      setDestino(codigo.toUpperCase());
    }
  }

  async function gravar() {
    if (!origem || !produto || !destino) return;
    setOcupado(true);
    try {
      const r = await rpc("wms_transferir", {
        p_codigo_origem: origem.codigo, p_codigo_destino: destino, p_codigo_produto: produto.codigo,
        p_quantidade: qtd, p_idem: novaChave(),
      });
      if (!r.ok) { sinalErro(); setErro(r.mensagem ?? "Transferência recusada"); return; }
      sinalConcluido();
      setOk(`${fmtNum(qtd)} ${produto.nome} de ${origem.codigo} para ${destino}`);
      setProduto(null);
      setDestino(null);
      await carregarOrigem(origem.codigo);
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  const rotulo = !origem ? "Bipe de onde vai tirar (posição ou carrinho)" : !produto ? "Bipe o produto a mover" : "Bipe a posição onde vai pôr";
  const daDoca = origem && ["RECEBIMENTO", "QUARENTENA"].includes(origem.tipo);

  return (
    <div className="flex flex-col gap-3">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      <h1 className="font-sinal text-3xl font-bold">Mover estoque</h1>
      {!origem && (
        <p className="text-aco-escuro">Tire de uma posição ou carrinho e ponha em outra posição: repor o picking à mão, juntar posições, devolver a sobra do carrinho.</p>
      )}
      {ok && <p className="rounded-md bg-ok-claro px-3 py-2 font-semibold text-ok">{ok}</p>}
      {daDoca && (
        <p className="rounded-md bg-info-claro px-3 py-2 text-info">
          Para guardar o que está na {origem.tipo === "QUARENTENA" ? "quarentena" : "doca"}, a <Link to={`/recebimento/armazenar?doca=${encodeURIComponent(origem.codigo)}`} className="font-semibold underline">Armazenagem</Link> diz onde guardar.
        </p>
      )}

      {origem && (
        <section className="rounded-lg border-2 border-tinta bg-chapa">
          <div className="flex items-center justify-between border-b-2 border-aco px-4 py-3">
            <span><span className="text-sm text-aco-escuro">{nomeTipoLocal[origem.tipo] ?? origem.tipo}</span>
              <span className="block font-sinal text-4xl font-bold">{origem.codigo}</span></span>
            <Botao variante="fantasma" onClick={() => { setOrigem(null); setProduto(null); setDestino(null); }}>Trocar</Botao>
          </div>
          {itens.length ? (
            <ul className="divide-y divide-aco">
              {itens.map((i) => (
                <li key={i.produto_id} className={`flex justify-between px-4 py-2 ${produto?.produto_id === i.produto_id ? "bg-faixa-clara" : ""}`}>
                  <span>{i.produtos.nome} <span className="text-aco-escuro">{i.produtos.codigo}</span></span>
                  <strong>{fmtNum(i.quantidade - i.reservado)}</strong>
                </li>
              ))}
            </ul>
          ) : <div className="p-4"><Vazio>Local vazio.</Vazio></div>}
        </section>
      )}

      {produto && destino ? (
        <section data-dialogo-aberto className="flex flex-col gap-3 rounded-lg bg-faixa p-4">
          <p className="text-lg font-semibold">{produto.nome}: {origem?.codigo} para <span className="font-sinal text-2xl">{destino}</span></p>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setQtd((q) => Math.max(1, q - 1))} className="h-14 w-14 rounded-md border-2 border-tinta bg-chapa text-2xl font-bold" aria-label="Menos um">-</button>
            <input type="number" inputMode="numeric" value={qtd} min={1} max={produto.disponivel}
              onChange={(e) => setQtd(Math.max(1, Math.min(produto.disponivel, Number(e.target.value))))}
              className="h-14 w-full rounded-md border-2 border-tinta bg-chapa text-center font-sinal text-3xl font-bold" aria-label="Quantidade" />
            <button type="button" onClick={() => setQtd((q) => Math.min(produto.disponivel, q + 1))} className="h-14 w-14 rounded-md border-2 border-tinta bg-chapa text-2xl font-bold" aria-label="Mais um">+</button>
          </div>
          <div className="flex gap-2">
            <Botao variante="secundario" onClick={() => setDestino(null)}>Voltar</Botao>
            <Botao variante="confirmar" grande className="flex-1" disabled={ocupado} onClick={() => void gravar()}>Mover {fmtNum(qtd)}</Botao>
          </div>
        </section>
      ) : (
        <LeituraCodigo rotulo={rotulo} onLeitura={leitura} desabilitado={ocupado} lampejo={lampejo} />
      )}
      <Link to="/estoque" className="text-center font-semibold underline">Voltar</Link>
    </div>
  );
}
